import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import FilterToolbar from './components/FilterToolbar';
import BulkOperations from './components/BulkOperations';
import IncidentsTable from './components/IncidentsTable';
import Pagination from './components/Pagination';
import QuickStats from './components/QuickStats';
import Icon from '../../components/AppIcon';

// Reusable component for displaying information blocks
const InfoBlock = ({ title, content }) => (
  <div className="space-y-1">
    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{title}</p>
    <p className="text-sm font-mono text-gray-700 dark:text-gray-300 break-all">{content}</p>
  </div>
);

const BlocksList = () => {
  const [data, setData] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_APP_SERVER_URL}/incident/blocks`);
        const responseData = await response.json();
        if (response.ok && responseData && !responseData.message) {
          setData(responseData);
        } else {
          setData([]);
        }
      } catch (error) {
        console.error("Failed to fetch blocks:", error);
        setData([]);
      }
    };
    fetchData();
  }, []);

  const mockUser = {
    id: 1,
    name: "Sarah Chen",
    email: "sarah.chen@cyberguard.gov",
    role: "Senior Analyst",
    permissions: ["view_dashboard", "view_incidents", "create_incident", "view_reports", "view_settings"]
  };
  const incidentCounts = {
    critical: 1,
    high: 2,
    medium: 3,
    low: 4
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={mockUser} incidentCounts={incidentCounts} />
      <main className="nav-offset max-w-full px-4 sm:px-6 lg:px-8 py-6">
        <div className="mt-10 mb-10">
          <h1 className="text-3xl font-semibold text-foreground">Blocks</h1>
        </div>
        <div className="max-w-7xl mx-auto mt-20">
          {data.length > 0 ? (
            <div className="space-y-8">
              {data.map((block, index) => (
                <div key={block.hash || index} className="w-full shadow-lg rounded-xl overflow-hidden transform transition-transform duration-300 hover:scale-[1.01] hover:shadow-2xl bg-white dark:bg-gray-900">
                  {/* CardHeader */}
                  <div className="bg-slate-100 dark:bg-slate-800 p-6 rounded-t-xl">
                    <h2 className="flex items-center justify-between text-2xl font-semibold text-gray-900 dark:text-white">
                      <span>Block #{block.height}</span>
                      {/* Badge */}
                      <span className="inline-flex items-center rounded-full bg-gray-50 dark:bg-gray-700 px-2.5 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                        Version: {block.protocolVersion}
                      </span>
                    </h2>
                    {/* CardDescription */}
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Author {block.author} on {new Date(block.timestamp).toLocaleString()}
                    </p>
                  </div>
                  {/* CardContent */}
                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <InfoBlock title="Hash" content={block.hash} />
                      <InfoBlock title="Parent Hash" content={block.parent?.hash || 'Genesis Block'} />
                      <InfoBlock title="Height" content={block.height} />
                    </div>
                    {/* Separator */}
                    <hr className="my-6 border-t border-gray-200 dark:border-gray-700" />
                    <div className="space-y-4">
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Transactions</h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {block.transactions && block.transactions.length > 0 ? (
                          block.transactions.map((tx, txIndex) => (
                            <div key={tx.hash || txIndex} className="bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 rounded-lg p-4 transition-shadow duration-200 hover:shadow-md">
                              <div className="p-0 space-y-1 text-sm text-gray-700 dark:text-gray-300">
                                <p><span className="font-medium text-gray-900 dark:text-white">Hash:</span> {tx.hash}</p>
                                <p><span className="font-medium text-gray-900 dark:text-white">Stage:</span> 
                                  <span className="inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:text-blue-300 ml-1">
                                    {tx.applyStage}
                                  </span>
                                </p>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-center text-gray-500 dark:text-gray-400 col-span-full">No transactions in this block.</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center p-10">
              <p className="text-lg text-foreground">No blocks found.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default BlocksList;